import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, homeRouteFor } from '../state/AuthContext';
import { useResource } from '../state/hooks';
import { ApiError, changePassword } from '../lib/api';
import type { DemoStatus } from '../lib/types';
import { Banner, Card, Field } from '../components/ui';

export function Login() {
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const demo = useResource<DemoStatus>('/demo/status');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Accounts the server chooses to advertise. Empty unless this instance was
  // explicitly configured to publish them.
  const accounts = demo.data?.testLogins ?? [];

  const attempt = async (withEmail: string, withPassword: string) => {
    setBusy(true);
    setError(null);
    try {
      await signIn(withEmail, withPassword);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign in');
      setBusy(false);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await attempt(email, password);
  };

  const quickSignIn = attempt;

  // A first-login or reset account cannot reach a console until it re-secures
  // itself; the server has already flagged it and every other page is gated.
  if (user?.mustChangePassword) return <ForcedPasswordChange />;
  if (user) {
    navigate(homeRouteFor(user), { replace: true });
    return null;
  }

  return (
    <div className="login-page">
      <div className="login-card stack">
        <div className="brand" style={{ fontSize: 26, justifyContent: 'center' }}>
          CAD<span>Computer-Aided Dispatch</span>
        </div>

        <Card>
          <form className="stack" onSubmit={submit}>
            {error && <Banner kind="error">{error}</Banner>}

            <Field label="Email">
              {(id) => (
                <input
                  id={id}
                  type="email"
                  autoComplete="username"
                  value={email}
                  required
                  autoFocus
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>

            <Field label="Password">
              {(id) => (
                <input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  required
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>

            <button className="btn primary lg block" type="submit" disabled={busy}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </Card>

        {accounts.length > 0 && (
          <Card title={demo.data?.isDemo ? 'Demo accounts' : 'Test accounts'}>
            <p className="faint" style={{ marginTop: 0 }}>
              {demo.data?.isDemo
                ? 'This is a sandbox holding fictional data. Pick a role to sign in.'
                : 'Shared test accounts. Pick a role to sign in — one click signs you straight in.'}
            </p>
            <ul className="list">
              {accounts.map((account) => (
                <li key={account.email}>
                  <button
                    type="button"
                    className="item"
                    disabled={busy}
                    onClick={() => {
                      // Fill the form as well as submitting, so a failure leaves
                      // the operator looking at the credentials that were tried.
                      setEmail(account.email);
                      setPassword(account.password);
                      void quickSignIn(account.email, account.password);
                    }}
                  >
                    <span className="grow">
                      <span className="item-title mono">{account.email}</span>
                      <span className="item-sub">{account.label}</span>
                    </span>
                    <span className="badge accent">Sign in</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="faint" style={{ marginBottom: 0 }}>
              Password for all of them: <code className="mono">{accounts[0].password}</code>
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function ForcedPasswordChange() {
  const { signOut } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (next !== confirm) {
      setError('The two new passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next);
      setDone(true);
      // Changing a password revokes every session, including this one.
      window.setTimeout(() => void signOut(), 1500);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <Card title="Choose a new password" className="login-card">
        {done ? (
          <Banner kind="ok">Password updated. Sign in again with your new password.</Banner>
        ) : (
          <form className="stack" onSubmit={submit}>
            <p className="faint" style={{ marginTop: 0 }}>
              This account is using a temporary password and must set its own before it can be used.
            </p>
            {error && <Banner kind="error">{error}</Banner>}
            <Field label="Temporary password">
              {(id) => (
                <input id={id} type="password" value={current} required onChange={(e) => setCurrent(e.target.value)} />
              )}
            </Field>
            <Field label="New password" hint="At least 10 characters.">
              {(id) => (
                <input
                  id={id}
                  type="password"
                  minLength={10}
                  value={next}
                  required
                  onChange={(e) => setNext(e.target.value)}
                />
              )}
            </Field>
            <Field label="Confirm new password">
              {(id) => (
                <input id={id} type="password" value={confirm} required onChange={(e) => setConfirm(e.target.value)} />
              )}
            </Field>
            <button className="btn primary block" type="submit" disabled={busy}>
              Update password
            </button>
          </form>
        )}
      </Card>
    </div>
  );
}
